import React, { FunctionComponent, useCallback, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { BaseButton } from '../../atoms/baseButton/BaseButton'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { CalendarEmptyIcon } from '../../atoms/icons/CalendarEmptyIcon'
import { CheckMarkOutOfCircleIcon } from '../../atoms/icons/CheckMarkOutOfCircleIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { ClinicalScreenerIllustration } from '../../atoms/illustrations/ClinicalScreenerIllustration'
import { OnsiteProvidersSearchIllustration } from '../../atoms/illustrations/OnsiteProvidersSearchIllustration'
import { Link } from '../../atoms/link/Link'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Subhead } from '../../atoms/subhead/Subhead'
import { BodyTextSize, SubheadSize } from '../../styles/typeStyles'
import { SpacedView } from '../../templates/content/CommonViews'
import { openUrl } from '../../utils/expo-linking'
import { tID } from '../../utils/utils'
import { Modal } from '../modal/Modal'

export interface OnsiteProviderInfoModalProps {
  isVisible?: boolean
  customerDisplayName?: string
  onContinue?: () => void
  sessionLengthInMinutes?: number
  onExit?: () => void
  customerPhoneNumber?: string
  isInternationalUser?: boolean
}

const ExitContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'end',
})

const HeaderContainer = styled(View)(({ theme: { spacing } }) => {
  return {
    paddingTop: spacing['16px'],
    alignItems: 'center',
  }
})

const ButtonContainer = styled(View)(({ theme: { spacing } }) => {
  return {
    alignItems: 'end',
    paddingTop: spacing['24px'],
    paddingBottom: spacing['24px'],
  }
})

const IconContainer = styled(View)(({ theme: { spacing } }) => {
  return {
    display: 'inline-flex',
    flexDirection: 'row',
    gap: spacing['24px'],
  }
})

const ExplainerContainer = styled(View)(({ theme: { spacing } }) => {
  return {
    gap: spacing['12px'],
  }
})
const ContactCareTeamContainer = styled(View)(({ theme: { spacing, colors } }) => {
  return {
    padding: spacing['16px'],
    backgroundColor: colors.backgroundSection,
    borderRadius: 8,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: spacing['8px'],
  }
})
const CareTeamDescriptionContainer = styled(View)(() => {
  return {
    flex: 1,
  }
})
const Header = styled(Subhead)({
  textAlign: 'center',
})

const ExplainerTextContainer = styled(View)({
  flexShrink: 1,
})

const ContinueButton = ({ onContinue, fullWidth = false }: { onContinue: () => void; fullWidth?: boolean }) => {
  const { spacing } = useTheme()
  const { formatMessage } = useIntl()

  return (
    <BaseButton
      fullWidth={fullWidth}
      onPress={onContinue}
      text={
        <FormattedMessage
          defaultMessage='Continue'
          description='Text for button to contine with booking onsite provider'
        />
      }
      accessibilityLabel={formatMessage({
        defaultMessage: 'Continue',
        description: 'Continue to book an onsite provider',
      })}
      style={{ paddingLeft: spacing['40px'], paddingRight: spacing['40px'] }}
    />
  )
}

const ImageContainer = styled(View)({
  alignItems: 'center',
})
export const OnsiteProviderInfoModal: FunctionComponent<OnsiteProviderInfoModalProps> = ({
  isVisible = false,
  customerDisplayName,
  onContinue = noop,
  sessionLengthInMinutes,
  onExit = noop,
  customerPhoneNumber,
  isInternationalUser = false,
}) => {
  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const openBottomSheet = useCallback(() => bottomSheetRef?.current?.expand(), [])
  const {
    colors,
    breakpoints: { isMobileSized },
  } = useTheme()

  return (
    <Modal
      modalContents={
        <SpacedView
          paddingLeft={isMobileSized ? '16px' : '0px'}
          paddingRight={isMobileSized ? '16px' : '0px'}
          paddingBottom={isMobileSized ? '16px' : '0px'}
          paddingTop={isMobileSized ? '16px' : '0px'}
        >
          <ExitContainer>
            <PressableOpacity onPress={onExit} testID={tID('OnsiteProviderInfoModal-xButton')}>
              <XIcon fillColor={colors.iconDefault} />
            </PressableOpacity>
          </ExitContainer>
          <ImageContainer>
            <OnsiteProvidersSearchIllustration size={isMobileSized ? 113 : 137} withBackground />
          </ImageContainer>
          <HeaderContainer>
            {customerDisplayName != null && (
              <Header
                size={SubheadSize.MEDIUM}
                text={
                  isInternationalUser ? (
                    <FormattedMessage
                      defaultMessage='Meet with an onsite counsellor at {customerDisplayName}'
                      description='Header for the modal displayed after clicking on the option to meet onsite therapists'
                      values={{ customerDisplayName }}
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage='Meet with an onsite therapist at {customerDisplayName}'
                      description='Header for the modal displayed after clicking on the option to meet onsite therapists'
                      values={{ customerDisplayName }}
                    />
                  )
                }
                style={{ textAlign: 'center' }}
              />
            )}
          </HeaderContainer>
          <SpacedView marginTop={isMobileSized ? '16px' : '24px'} paddingBottom={isMobileSized ? '16px' : '32px'}>
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='Onsite providers understand your company culture and are here to support you. Book sessions in-office or by video.'
                  description='Informational about onsite providers shown in modal before members search for onsite providers.'
                />
              }
            />
          </SpacedView>
          <ExplainerContainer>
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='How it works'
                  description='Header of the breakdown for how onsite providers work'
                />
              }
            />
            <IconContainer>
              <View>
                <CheckMarkOutOfCircleIcon />
              </View>

              <ExplainerTextContainer>
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='<bold>Select</bold> your office to find available onsite providers.'
                      description='First bullet for information about onsite providers describing the first step'
                      values={{
                        bold: (text: string) => (
                          <BodyText size={BodyTextSize.DEFAULT} bold>
                            {text}
                          </BodyText>
                        ),
                      }}
                    />
                  }
                />
              </ExplainerTextContainer>
            </IconContainer>
            <IconContainer>
              <View>
                <CalendarEmptyIcon fillColor={colors.iconActive} strokeColor={colors.iconActive} />
              </View>
              <ExplainerTextContainer>
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='<bold>Book</bold> regular {sessionLengthInMinutes}-minute sessions at a time that works for you.'
                      description='First bullet for information about onsite providers describing the first step'
                      values={{
                        bold: (text: string) => (
                          <BodyText size={BodyTextSize.DEFAULT} bold>
                            {text}
                          </BodyText>
                        ),
                        sessionLengthInMinutes,
                      }}
                    />
                  }
                />
              </ExplainerTextContainer>
            </IconContainer>
          </ExplainerContainer>
          <ButtonContainer>
            <View>{!isMobileSized && onContinue != null && <ContinueButton onContinue={onContinue} />}</View>
          </ButtonContainer>
          <ContactCareTeamContainer>
            {!isMobileSized && (
              <View>
                <ClinicalScreenerIllustration width={76} height={55} />
              </View>
            )}
            <CareTeamDescriptionContainer>
              <View>
                <Subhead
                  size={SubheadSize.XSMALL}
                  text={
                    <FormattedMessage
                      defaultMessage='Need help right now?'
                      description='Title for the section on how to reach our Clinical Care Navigators.'
                    />
                  }
                />
              </View>
              <View>
                <BodyText
                  text={
                    isInternationalUser ? (
                      <FormattedMessage
                        defaultMessage='Our Clinical Care Navigators are always available for immediate crisis support at <link>{phone}</link>.'
                        description='Description of how a Care Navigator can give the user support any time and also informs that clinicians are available. The Care Navigator can also help users search for care, finding the right provider, and troubleshoot technical issues.'
                        values={{
                          phone: customerPhoneNumber,
                          link: (phone: string) => (
                            <Link text={phone} underline onPress={() => openUrl(`tel:${phone}`)} />
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage='Our Clinical Care Navigators are always available for immediate crisis support at <link>{phone}</link>, option 2.'
                        description='Description of how a Care Navigator can give the user support any time and also informs that clinicians are available. The Care Navigator can also help users search for care, finding the right provider, and troubleshoot technical issues.'
                        values={{
                          phone: customerPhoneNumber,
                          link: (phone: string) => (
                            <Link text={phone} underline onPress={() => openUrl(`tel:${phone}`)} />
                          ),
                        }}
                      />
                    )
                  }
                  size={BodyTextSize.SMALL}
                />
              </View>
            </CareTeamDescriptionContainer>
          </ContactCareTeamContainer>
          {isMobileSized && (
            <SpacedView paddingTop='20px'>
              <ContinueButton onContinue={onContinue} fullWidth />
            </SpacedView>
          )}
        </SpacedView>
      }
      visible={isVisible}
      bottomSheetRef={bottomSheetRef}
      onRequestClose={onExit}
      onLayout={openBottomSheet}
      width='624'
      useDynamicHeightForBottomSheet
    ></Modal>
  )
}
